/**
 * web/src/components/speaker/InviteThreadPicker.tsx
 * Kickoff thread-picker (design §5): a collapsed-by-default "Reply on an
 * existing thread" section for the Start-onboarding dialog. Expanding it fetches
 * the reviewer's recent Gmail threads with the speaker (via useInviteThreads)
 * and offers radio-style selection — pick a thread to send the invite AS A REPLY
 * on it (In-Reply-To its real Message-ID, keeping the subject), or leave the
 * default "Start a new thread". Skeleton loader while fetching; an inline error
 * (or an empty-state) when the fetch fails or returns nothing. Selection is a
 * `${connectionId} ${threadId}` string (null = fresh thread); the dialog maps
 * it to the start payload's `inviteThread`.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import type { InviteThreadCandidate } from '../../api/speakers';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { formatUtcIsoLocal } from '../../lib/dates';

/** Stable selection token so a pick uniquely names (mailbox, thread). */
export function threadToken(t: InviteThreadCandidate): string {
  return `${t.connectionId} ${t.threadId}`;
}

/** One selectable row: a radio dot + subject + snippet + mailbox/date line. */
function ThreadRow({
  thread,
  selected,
  onSelect,
}: {
  thread: InviteThreadCandidate;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'w-full rounded-lg border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-type-speaker/40 bg-type-speaker-soft/40 shadow-1'
          : 'border-border hover:bg-bg-hover',
      )}
    >
      <span className="flex items-start gap-2">
        <span
          className={cn(
            'mt-0.5 grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border',
            selected ? 'border-type-speaker bg-type-speaker text-white' : 'border-border-strong bg-bg',
          )}
        >
          {selected && <Icon name="check" size={10} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold">{thread.subject || '(no subject)'}</span>
          {thread.snippet && (
            <span className="mt-0.5 block truncate text-xs text-text-muted">{thread.snippet}</span>
          )}
          <span className="mt-0.5 block truncate text-[11px] text-text-faint">
            {thread.mailbox}
            {thread.lastMessageAt ? ` · ${formatUtcIsoLocal(thread.lastMessageAt)}` : ''}
          </span>
        </span>
      </span>
    </button>
  );
}

/** The collapsible thread-picker section. */
export function InviteThreadPicker({
  open,
  onToggle,
  speakerEmail,
  query,
  selected,
  onSelect,
  className,
}: {
  open: boolean;
  onToggle: () => void;
  speakerEmail: string | null;
  query: UseQueryResult<InviteThreadCandidate[]>;
  /** `${connectionId} ${threadId}` of the picked thread, or null for fresh. */
  selected: string | null;
  onSelect: (token: string | null) => void;
  className?: string;
}) {
  const threads = query.data ?? [];
  return (
    <div className={cn('rounded-lg border border-border', className)}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold"
      >
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={13} className="shrink-0 text-text-muted" />
        <Icon name="mail" size={13} className="shrink-0 text-text-muted" />
        <span>Reply on an existing thread</span>
        {selected !== null && (
          <span className="ml-auto rounded-full border border-type-speaker/40 bg-type-speaker-soft px-2 py-px text-[10.5px] font-semibold text-type-speaker">
            1 selected
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-border px-3 py-2.5">
          {!speakerEmail ? (
            <p className="text-xs text-text-muted">
              No email on this speaker — the invite starts a new thread.
            </p>
          ) : query.isPending ? (
            <div className="flex flex-col gap-2" aria-busy="true">
              <div className="h-9 w-full animate-pulse rounded bg-bg-muted" />
              <div className="h-9 w-full animate-pulse rounded bg-bg-muted" />
            </div>
          ) : query.isError ? (
            <p className="text-xs text-danger">
              Couldn&rsquo;t load your Gmail threads — the invite will start a new thread.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => onSelect(null)}
                aria-pressed={selected === null}
                className={cn(
                  'w-full rounded-lg border px-3 py-2 text-left text-[13px] font-semibold transition-colors',
                  selected === null
                    ? 'border-type-speaker/40 bg-type-speaker-soft/40 shadow-1'
                    : 'border-border hover:bg-bg-hover',
                )}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={cn(
                      'grid h-[15px] w-[15px] shrink-0 place-items-center rounded-full border',
                      selected === null
                        ? 'border-type-speaker bg-type-speaker text-white'
                        : 'border-border-strong bg-bg',
                    )}
                  >
                    {selected === null && <Icon name="check" size={10} />}
                  </span>
                  Start a new thread
                </span>
              </button>
              {threads.length === 0 ? (
                <p className="px-1 pt-1 text-xs text-text-muted">
                  No recent threads with {speakerEmail} in your mailboxes.
                </p>
              ) : (
                threads.map((t) => {
                  const token = threadToken(t);
                  return (
                    <ThreadRow
                      key={token}
                      thread={t}
                      selected={selected === token}
                      onSelect={() => onSelect(token)}
                    />
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
